import React, { ChangeEvent, ComponentProps, CSSProperties, FocusEvent } from "react";
import { Field } from "redux-form/immutable";
import type {
  BaseFieldProps,
  WrappedFieldInputProps,
  WrappedFieldMetaProps
} from "redux-form";
import styled from "styled-components";
import { FormattedMessage } from "react-intl";

import UpInput from "../up-input";
import UpFieldWithErrorState from "../UpFieldWithErrorState";
import globalMessages from "../../messages/globalMessages";

const SpecialInput = styled(UpInput)`
  border: 1px solid transparent;
  background: none;
  padding: 6px 10px;
  flex: 0;
`;

const LeftEndInput = styled(SpecialInput)`
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  max-width: 28px;
`;

interface MiddleInputProps {
  flex?: CSSProperties["flex"];
  maxWidth?: CSSProperties["maxWidth"];
}

const MiddleInput = styled(SpecialInput)<MiddleInputProps>`
  border-radius: 0px;
  flex: ${(props) => props.flex || 0};
  max-width: ${(props) => props.maxWidth || ""};
`;

const RightEndInput = styled(SpecialInput)`
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  max-width: 40px;
`;

const FakeField = styled.div`
  border: ${(props) => props.theme.inputBorder};
  &.error {
    border-color: ${(props) => props.theme.primaryActionColor};
    background-color: #f5d1d2;
  }
  border-radius: 3px;
  background: #fff;
  display: flex;
`;

type RegexValidationError = "regexError";

const regexValidation = (value?: string): RegexValidationError | undefined => {
  if (value && value !== "") {
    const splittedValue = value.split("/");
    let isValid = true;

    try {
      new RegExp(splittedValue[1] || "", splittedValue[2]);
    } catch {
      isValid = false;
    }

    if (!isValid) {
      return "regexError";
    }
    if (!splittedValue[1]) {
      return "regexError";
    }
  }

  return undefined;
};

/**
 * Bugticket UP-1012
 *
 * Because negative look behind is not available
 * we have to use negative look ahead and reverse
 * the string to ensure that escaped slashes are
 * not split.
 *
 * a(?!b) is a look ahead that matches all a's which
 * are not followed by the letter b.
 */
const splitOnSlash = (value: string): string[] => {
  const reversedValue = value.split("").reverse().join("");
  const splitValue = reversedValue.split(/\/(?!\\)/g);

  return splitValue
    .map((part) => part.split("").reverse().join(""))
    .reverse();
};

type RegexFieldInputProps = Pick<
  WrappedFieldInputProps,
  "onBlur" | "onChange" | "onFocus"
> & {
  value: string;
};

type RegexFieldMetaProps = Omit<
  Pick<WrappedFieldMetaProps, "error" | "touched">,
  "error"
> & {
  error?: keyof typeof globalMessages;
};

type RegexFieldLayoutProps = Pick<
  ComponentProps<typeof UpFieldWithErrorState>,
  "label" | "margin" | "width"
>;

interface RegexFieldProps extends RegexFieldLayoutProps {
  input: RegexFieldInputProps;
  meta: RegexFieldMetaProps;
}

interface RegexFieldState {
  regex: string;
  flags: string;
}

class RegexField extends React.Component<RegexFieldProps, RegexFieldState> {
  constructor(props: RegexFieldProps) {
    super(props);

    this.state = {
      regex: splitOnSlash(props.input.value)[1] || "",
      flags: splitOnSlash(props.input.value)[2] || ""
    };
  }

  handleMainRegex(value: string): void {
    this.setState({ regex: value });
    this.props.input.onChange(`/${value}/${this.state.flags}`);
  }

  handleFlagsRegex(value: string): void {
    this.setState({ flags: value });
    this.props.input.onChange(`/${this.state.regex}/${value}`);
  }

  render() {
    const { input, meta, label, margin, width } = this.props;
    const hasError = meta.error !== undefined && meta.touched;
    const errorMessage = meta.error ? (
      <FormattedMessage {...globalMessages[meta.error]} />
    ) : (
      ""
    );

    return (
      <div style={{ width: width || "auto" }}>
        <UpFieldWithErrorState
          error={hasError}
          errorMessage={errorMessage}
          margin={margin}
          width={width}
          label={label}
        >
          <FakeField className={meta.error && meta.touched ? "error" : undefined}>
            <LeftEndInput value="/" disabled width="28px" />
            <MiddleInput
              placeholder="RegEx Expression" // If change placeholder, don't forget to change it for test/UpRegex.test.js too
              flex="1 0 auto"
              size={1}
              value={this.state.regex}
              onFocus={(event: FocusEvent<HTMLInputElement>) => input.onFocus(event)}
              onBlur={(event: FocusEvent<HTMLInputElement>) =>
                input.onBlur(`/${event.target.value}/${this.state.flags}`)
              }
              name="regex"
              type="text"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                this.handleMainRegex(event.target.value)
              }
            />
            <MiddleInput value="/" disabled width="28px" maxWidth="28px" />
            <RightEndInput
              placeholder="Flags" // If change placeholder, don't forget to change it for test/UpRegex.test.js too
              value={this.state.flags}
              onFocus={(event: FocusEvent<HTMLInputElement>) => input.onFocus(event)}
              onBlur={(event: FocusEvent<HTMLInputElement>) =>
                input.onBlur(`/${this.state.regex}/${event.target.value}`)
              }
              name="flags"
              width="40px"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                this.handleFlagsRegex(event.target.value)
              }
            />
          </FakeField>
        </UpFieldWithErrorState>
      </div>
    );
  }
}

type UpRegexProps = Pick<BaseFieldProps, "name"> &
  RegexFieldLayoutProps & {
    fieldValidation?: boolean;
  };

const UpRegex: React.FC<UpRegexProps> = ({
  name,
  label,
  margin,
  width,
  fieldValidation = true
}) => (
  <Field
    name={name}
    type="text"
    component={RegexField}
    label={label}
    validate={fieldValidation ? [regexValidation] : []}
    margin={margin}
    width={width}
  />
);

export default UpRegex;
